/**
 * useCopilotVoice — Conversa contínua por voz com o Copilot.
 *
 * Captura áudio com VAD, transcreve via jarvis-pipeline (que tem fallback Whisper→Gemini),
 * envia o texto para admin-copilot e reproduz a resposta com voz Sarah (ElevenLabs).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { createVAD, type VADController } from "@/lib/jarvis/vad";

export type CopilotVoiceState = "idle" | "starting" | "listening" | "thinking" | "speaking" | "error";

export interface UseCopilotVoiceOptions {
  history: Array<{ role: "user" | "assistant"; content: string }>;
  onUserTranscript?: (text: string) => void;
  onAssistantText?: (text: string) => void;
  onError?: (msg: string) => void;
}

export function useCopilotVoice(opts: UseCopilotVoiceOptions) {
  const { history, onUserTranscript, onAssistantText, onError } = opts;
  const [state, setState] = useState<CopilotVoiceState>("idle");
  const [level, setLevel] = useState(0);

  const vadRef = useRef<VADController | null>(null);
  const audioElRef = useRef<HTMLAudioElement | null>(null);
  const inFlightRef = useRef<AbortController | null>(null);
  const stateRef = useRef<CopilotVoiceState>("idle");
  const historyRef = useRef(history);

  useEffect(() => { stateRef.current = state; }, [state]);
  useEffect(() => { historyRef.current = history; }, [history]);

  useEffect(() => {
    const el = new Audio();
    el.preload = "auto";
    audioElRef.current = el;
    return () => { el.pause(); el.src = ""; audioElRef.current = null; };
  }, []);

  const blobToBase64 = (blob: Blob): Promise<string> =>
    new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => {
        const result = r.result as string;
        resolve(result.split(",")[1] ?? "");
      };
      r.onerror = () => reject(r.error);
      r.readAsDataURL(blob);
    });

  const handleSpeechStart = useCallback(() => {
    // Barge-in: se Sarah estava falando, interrompe
    if (stateRef.current === "speaking" || stateRef.current === "thinking") {
      audioElRef.current?.pause();
      if (audioElRef.current) audioElRef.current.currentTime = 0;
      inFlightRef.current?.abort("barge_in");
    }
    setState("listening");
  }, []);

  const transcribeAudio = useCallback(async (audioBlob: Blob): Promise<string | null> => {
    const audioBase64 = await blobToBase64(audioBlob);
    if (!audioBase64 || audioBase64.length < 100) return null;

    const { data, error } = await supabase.functions.invoke("copilot-stt", {
      body: {
        audio_base64: audioBase64,
        mimetype: audioBlob.type || "audio/webm",
        language: "pt-BR",
      },
    });
    if (error) {
      console.warn("[useCopilotVoice] stt error", error);
      return null;
    }
    return (data?.text as string) || null;
  }, []);

  const speak = useCallback(async (text: string) => {
    setState("speaking");
    try {
      // Usa Web Speech API (sem dependência de edge function)
      if (!("speechSynthesis" in window)) {
        setState("listening");
        return;
      }
      window.speechSynthesis.cancel();
      const utter = new SpeechSynthesisUtterance(text);
      utter.lang = "pt-BR";
      utter.rate = 1.0;
      utter.pitch = 1.0;
      utter.onend = () => setState("listening");
      utter.onerror = () => setState("listening");
      window.speechSynthesis.speak(utter);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      console.error("[useCopilotVoice] speak failed", msg);
      onError?.(msg);
      setState("listening");
    }
  }, [onError]);

  const handleSpeechEnd = useCallback(async (audioBlob: Blob, durationMs: number) => {
    if (audioBlob.size < 2000) { setState("listening"); return; }

    setState("thinking");
    inFlightRef.current?.abort("superseded");
    const ctrl = new AbortController();
    inFlightRef.current = ctrl;

    try {
      const userText = await transcribeAudio(audioBlob);
      if (ctrl.signal.aborted) return;
      if (!userText || userText.trim().length < 2) {
        setState("listening");
        return;
      }
      onUserTranscript?.(userText);

      const newHistory = [
        ...historyRef.current,
        { role: "user" as const, content: userText },
      ];

      const { data, error } = await supabase.functions.invoke("admin-copilot", {
        body: { question: userText, history: newHistory.slice(-6) },
      });
      if (ctrl.signal.aborted) return;
      if (error) throw new Error(error.message || "copilot_failed");

      const answer = (data?.answer as string) || "Sem resposta.";
      onAssistantText?.(answer);

      await speak(answer);
    } catch (err) {
      if (ctrl.signal.aborted) return;
      const msg = err instanceof Error ? err.message : String(err);
      console.error("[useCopilotVoice] turn error", msg);
      onError?.(msg);
      setState("listening");
    } finally {
      if (inFlightRef.current === ctrl) inFlightRef.current = null;
    }
  }, [transcribeAudio, speak, onUserTranscript, onAssistantText, onError]);

  const start = useCallback(async () => {
    if (vadRef.current?.isActive()) return;
    setState("starting");
    try {
      const vad = createVAD({
        onSpeechStart: handleSpeechStart,
        onSpeechEnd: handleSpeechEnd,
        onLevel: setLevel,
      });
      await vad.start();
      vadRef.current = vad;
      setState("listening");
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      console.error("[useCopilotVoice] start failed", msg);
      onError?.(msg);
      setState("error");
    }
  }, [handleSpeechStart, handleSpeechEnd, onError]);

  const stop = useCallback(() => {
    vadRef.current?.stop();
    vadRef.current = null;
    audioElRef.current?.pause();
    if ("speechSynthesis" in window) window.speechSynthesis.cancel();
    inFlightRef.current?.abort("stopped");
    inFlightRef.current = null;
    setState("idle");
    setLevel(0);
  }, []);

  useEffect(() => () => stop(), [stop]);

  return { start, stop, speak, state, level };
}
